import React, { useEffect, useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination, Autoplay } from "swiper/modules";
import ProductCard from "./ProductCard";

// Import Swiper styles
import "swiper/css";
import "swiper/css/pagination";

export default function UserPageProdSlider() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;

  // Helper function to get the lowest quantity and its price
  const getLowestQuantityPrice = (prices) => {
    if (!prices || typeof prices !== 'object') return { quantity: null, price: 0 };
    
    const quantities = Object.keys(prices).map(qty => {
      const numericValue = parseInt(qty.replace(/ml$/, ''));
      return { key: qty, value: numericValue, price: Number(prices[qty]) };
    });
    
    // Sort by quantity value to get the smallest
    quantities.sort((a, b) => a.value - b.value);
    
    return quantities.length > 0 ? {
      quantity: quantities[0].key,
      price: quantities[0].price
    } : { quantity: null, price: 0 };
  };

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/list-products`);
        if (!response.ok) {
          throw new Error("Network response was not ok");
        }
        const data = await response.json();
        
        // Transform API data to match ProductCard structure
        const formattedProducts = data.products.map(product => {
          // Get lowest quantity price for both regular and discounted prices
          const lowestRegularPrice = getLowestQuantityPrice(product.prices);
          const lowestDiscountedPrice = product.discountedPrices ? 
                                       getLowestQuantityPrice(product.discountedPrices) : 
                                       null;
          
          // Check if product has discounted prices for the same quantity
          const hasDiscount = lowestDiscountedPrice && 
                             lowestDiscountedPrice.price > 0 &&
                             lowestDiscountedPrice.price < lowestRegularPrice.price;
          
          const basePrice = lowestRegularPrice.price;
          const discountedPrice = hasDiscount ? lowestDiscountedPrice.price : basePrice;
          
          // Calculate discount percentage if there's a discount
          const discount = hasDiscount ? 
                         Math.round(((basePrice - discountedPrice) / basePrice) * 100) :
                         0;

          return {
            id: product.id,
            image: product.images[0], // Use the first image
            name: product.name,
            fragranceType: product.fragranceType, // Include fragrance type (Perfumes/Attars)
            category: product.category,
            quantity: lowestRegularPrice.quantity, // Include the quantity being displayed
            originalPrice: basePrice,
            discountedPrice: discountedPrice,
            discount: discount > 0 ? discount : null, // Only include if there's a discount
            isNewArrival: product.isNewArrival,
            stockAvailability: product.stockAvailability
          };
        });
        
        // Limit to 10 products as requested
        setProducts(formattedProducts.slice(0, 10));
      } catch (error) {
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, []);

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-500"></div>
        <p className="mt-2 text-gray-600">Loading products...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <div className="text-red-500 mb-2">⚠️ Error loading products</div>
        <p className="text-gray-600 text-sm">{error}</p>
        <button 
          onClick={() => window.location.reload()} 
          className="mt-3 px-4 py-2 bg-yellow-500 text-black rounded hover:bg-yellow-600 transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  if (!products.length) {
    return (
      <div className="text-center py-8">
        <div className="text-gray-500 mb-2">📦 No products available</div>
        <p className="text-gray-600 text-sm">Check back later for new arrivals</p>
      </div>
    );
  }

  return (
    <div className="w-full px-5 md:px-8 py-2">
      {/* Products count indicator */}
      <div className="text-center mb-4">
        <p className="text-sm text-gray-600">
          Showing {products.length} featured products
        </p>
      </div>

      <Swiper
        modules={[Pagination, Autoplay]}
        spaceBetween={30}
        slidesPerView={1}
        pagination={{
          clickable: true,
          renderBullet: (index, className) => {
            return `<span class="${className} w-2 h-2 bg-gray-400 rounded-full mx-1 hover:bg-yellow-500 transition-colors"></span>`;
          },
        }}
        autoplay={{
          delay: 4000,
          disableOnInteraction: false,
          pauseOnMouseEnter: true,
        }}
        breakpoints={{
          480: { slidesPerView: 1, spaceBetween: 15 },
          768: { slidesPerView: 2, spaceBetween: 20 },
          1024: { slidesPerView: 3, spaceBetween: 25 },
          1280: { slidesPerView: 4, spaceBetween: 30 },
          1536: { slidesPerView: 5, spaceBetween: 35 }, // Added for larger screens
        }}
        className="product-swiper"
      >
        {products.map((product, index) => (
          <SwiperSlide key={`${product.id}-${index}`} className="py-4">
            <div className="transform transition-transform duration-300 hover:scale-105">
              <ProductCard product={product} />
            </div>
          </SwiperSlide>
        ))}
      </Swiper>
      
      {/* View All Products Button */}
      <div className="text-center mt-6">
        <a 
          href="/shop"
          className="inline-block bg-[#D4AF37] hover:bg-[#C19B2E] text-black py-3 px-6 rounded-sm transition-colors duration-300 shadow-md hover:shadow-lg transform hover:scale-105"
        >
          View All Products
        </a>
      </div>
    </div>
  );
}
